import { Info, Zap } from 'lucide-react'
import { getDisplayName, getInformalName, getTeamLogo, formatOdds } from '@/lib/teams'

interface TeamSelectButtonProps {
  teamName: string
  teamLogo: string
  form?: string
  rank?: number  // Team's position in league standings
  odds: number | undefined
  usage: number
  isSelected: boolean
  isDisabled: boolean
  tddEnabled: boolean
  tddEligible: boolean
  canUseTdd: boolean
  variant?: 'row' | 'column'
  onSelect?: () => void
  onDeselect?: () => void
  onToggleTdd?: (enabled: boolean) => void
  onViewInfo?: () => void
}

/** Render form string as colored badges */
function FormBadges({ form, size = 'default' }: { form?: string; size?: 'default' | 'small' }) {
  if (!form) return null
  // Show last 3 results (form string is chronological: oldest first, newest last)
  const results = form.slice(-3).split('')
  const sizeClasses = size === 'small' 
    ? 'w-5 h-5 text-[10px]' 
    : 'w-6 h-6 text-xs'
  return (
    <div className="flex items-center gap-0.5">
      {results.map((result, i) => {
        let bg = 'bg-zinc-200 dark:bg-zinc-700'
        let text = 'text-zinc-500 dark:text-zinc-400'
        if (result === 'W') {
          bg = 'bg-emerald-100 dark:bg-emerald-900/50'
          text = 'text-emerald-700 dark:text-emerald-400'
        } else if (result === 'L') {
          bg = 'bg-red-100 dark:bg-red-900/50'
          text = 'text-red-700 dark:text-red-400'
        } else if (result === 'D') {
          bg = 'bg-zinc-200 dark:bg-zinc-700'
          text = 'text-zinc-600 dark:text-zinc-300'
        }
        return (
          <span
            key={i}
            className={`${sizeClasses} flex items-center justify-center font-bold rounded ${bg} ${text}`}
          >
            {result}
          </span>
        )
      })}
    </div>
  )
}

export function TeamSelectButton({
  teamName,
  teamLogo,
  form,
  rank,
  odds,
  usage,
  isSelected,
  isDisabled,
  tddEnabled,
  tddEligible,
  canUseTdd,
  variant = 'row',
  onSelect,
  onDeselect,
  onToggleTdd,
  onViewInfo,
}: TeamSelectButtonProps) {
  const displayName = getDisplayName(teamName)
  const informalName = getInformalName(teamName)
  const isMaxUsage = usage >= 2

  const handleClick = () => {
    if (isDisabled || isMaxUsage) return
    if (isSelected) {
      onDeselect?.()
    } else {
      onSelect?.()
    }
  }

  const borderClasses = isSelected
    ? 'bg-emerald-50 dark:bg-emerald-950/50 border-emerald-500 dark:border-emerald-600 ring-2 ring-emerald-500/20'
    : isDisabled || isMaxUsage
      ? 'bg-zinc-100 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700 opacity-50'
      : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'

  // Column variant — vertical card layout (tablet+)
  if (variant === 'column') {
    return (
      <div className={`relative flex flex-col items-center p-3 rounded-xl border transition-all w-full min-w-0 ${borderClasses}`}>
        {/* DD Eligible Indicator (unselected) */}
        {!isSelected && tddEligible && !isMaxUsage && !isDisabled && (
          <div className="absolute -top-2 -left-2 z-10">
            <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300 shadow-sm">
              <Zap className="w-2.5 h-2.5" />
              DD
            </span>
          </div>
        )}

        {/* Info button */}
        <button
          onClick={(e) => { e.stopPropagation(); onViewInfo?.() }}
          className="absolute top-1.5 right-1.5 p-1 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100
                     dark:text-zinc-500 dark:hover:text-zinc-300 dark:hover:bg-zinc-800 transition-colors"
          title="View team info"
        >
          <Info className="w-3.5 h-3.5" />
        </button>

        {/* Clickable team area */}
        <button
          onClick={handleClick}
          disabled={isDisabled || isMaxUsage}
          className="w-full text-center flex-1 min-w-0"
        >
          {/* Logo */}
          {teamLogo.startsWith('http') ? (
            <img src={teamLogo} alt="" className="w-10 h-10 mx-auto" />
          ) : (
            <span className="text-3xl block">{teamLogo}</span>
          )}

          {/* Team Name */}
          <p className={`font-semibold text-sm leading-tight mt-1.5 ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-zinc-900 dark:text-zinc-100'}`}
             title={displayName}>
            {informalName}
          </p>

          {/* Rank + Form badges + Usage */}
          <div className="flex items-center justify-center gap-1.5 mt-1">
            {rank != null && (
              <span className="text-xs text-zinc-400 dark:text-zinc-500">
                #{rank}
              </span>
            )}
            <FormBadges form={form} size="small" />
            <span className={`text-xs font-medium ${isMaxUsage ? 'text-red-500 dark:text-red-400' : 'text-zinc-500 dark:text-zinc-400'}`}>
              {usage}/2
            </span>
          </div>

          {/* Odds */}
          <div className="flex items-center justify-center mt-1.5">
            {odds != null && (
              <span className="text-sm font-mono font-bold text-zinc-800 dark:text-zinc-200">
                {formatOdds(odds)}
              </span>
            )}
          </div>
        </button>

        {/* Select / Selected Button */}
        <div className="mt-2 w-full">
          <button
            onClick={handleClick}
            disabled={!isSelected && (isDisabled || isMaxUsage)}
            className={`w-full px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
              ${isSelected
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : isDisabled || isMaxUsage
                  ? 'bg-zinc-200 text-zinc-400 dark:bg-zinc-700 dark:text-zinc-500 cursor-not-allowed'
                  : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-emerald-100 hover:text-emerald-700 dark:hover:bg-emerald-900/50 dark:hover:text-emerald-300'
              }`}
          >
            {isSelected ? 'Selected' : isMaxUsage ? 'Maxed' : 'Select'}
          </button>
          {isSelected && tddEligible && canUseTdd && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggleTdd?.(!tddEnabled) }}
              className={`w-full mt-1.5 flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-all
                ${tddEnabled
                  ? 'bg-violet-500 text-white'
                  : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400 hover:bg-violet-50 dark:hover:bg-violet-900/30'
                }`}
            >
              <Zap className="w-3 h-3" />
              DD {tddEnabled ? 'ON' : 'OFF'}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Row variant (default) — horizontal layout for mobile
  // Compact design: Logo | Name + Form + Usage | Odds | Info | Select
  return (
    <div className={`relative flex items-center gap-2 p-2 rounded-xl border transition-all ${borderClasses}`}>
      {/* DD Eligible Indicator */}
      {!isSelected && tddEligible && !isMaxUsage && !isDisabled && (
        <div className="absolute -top-2 -left-2 z-10">
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300 shadow-sm">
            <Zap className="w-2.5 h-2.5" />
            DD
          </span>
        </div>
      )}

      {/* Team Logo */}
      <button
        onClick={handleClick}
        disabled={isDisabled || isMaxUsage}
        className="shrink-0"
      >
        {teamLogo.startsWith('http') ? (
          <img src={teamLogo} alt="" className="w-10 h-10" />
        ) : (
          <span className="text-3xl block w-10 h-10 flex items-center justify-center">{teamLogo}</span>
        )}
      </button>

      {/* Team Info - Name, Rank, Form, Usage */}
      <button
        onClick={handleClick}
        disabled={isDisabled || isMaxUsage}
        className="flex-1 min-w-0 text-left"
      >
        <p className={`font-semibold text-base leading-tight ${isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-zinc-900 dark:text-zinc-100'}`}
           title={displayName}>
          {informalName}
        </p>
        <div className="flex items-center gap-1.5 mt-1">
          {rank != null && (
            <span className="text-xs text-zinc-400 dark:text-zinc-500">
              #{rank}
            </span>
          )}
          <FormBadges form={form} size="small" />
          <span className={`text-xs font-medium ${isMaxUsage ? 'text-red-500 dark:text-red-400' : 'text-zinc-500 dark:text-zinc-400'}`}>
            {usage}/2
          </span>
        </div>
      </button>

      {/* Odds */}
      <div className="flex items-center shrink-0">
        {odds != null && (
          <span className="text-sm font-mono font-bold text-zinc-700 dark:text-zinc-300">
            {formatOdds(odds)}
          </span>
        )}
      </div>

      {/* Info button */}
      <button
        onClick={(e) => { e.stopPropagation(); onViewInfo?.() }}
        className="p-1 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100
                   dark:text-zinc-500 dark:hover:text-zinc-300 dark:hover:bg-zinc-800 transition-colors shrink-0"
        title="View team info"
      >
        <Info className="w-4 h-4" />
      </button>

      {/* Select Button - fixed width to prevent layout shift */}
      <div className="shrink-0 w-[76px]">
        <button
          onClick={handleClick}
          disabled={!isSelected && (isDisabled || isMaxUsage)}
          className={`w-full py-1.5 text-sm font-medium rounded-lg transition-colors text-center
            ${isSelected
              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
              : isDisabled || isMaxUsage
                ? 'bg-zinc-200 text-zinc-400 dark:bg-zinc-700 dark:text-zinc-500 cursor-not-allowed'
                : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-emerald-100 hover:text-emerald-700 dark:hover:bg-emerald-900/50 dark:hover:text-emerald-300'
            }`}
        >
          {isSelected ? 'Selected' : isMaxUsage ? 'Maxed' : 'Select'}
        </button>
      </div>

      {/* DD Toggle - shown below when selected */}
      {isSelected && tddEligible && canUseTdd && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onToggleTdd?.(!tddEnabled)
          }}
          className={`absolute -bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full transition-all shadow-sm
            ${tddEnabled
              ? 'bg-violet-500 text-white'
              : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400 hover:bg-violet-100 dark:hover:bg-violet-900/30'
            }`}
        >
          <Zap className="w-2.5 h-2.5" />
          DD {tddEnabled ? 'ON' : 'OFF'}
        </button>
      )}
    </div>
  )
}
